import React, { useMemo } from 'react';
import { defaultMusicVideoDoc, parseMusicVideoDoc } from 'fym-shared/src/music24/musicVideoDoc';
import type { MusicVideoSongMap } from 'fym-shared/src/music24/musicVideoApi';
import type { MusicVideoShowcaseResponse } from 'fym-shared/src/music24/musicVideoShowcase';
import { TypedMemo } from '../../common/types';
import { MusicVideoEditor } from '../MusicVideoEditor';
import { SHOWCASE_ITEM_KEY } from './musicVideoDemoPath';
import { MusicVideoShowcaseContext } from './MusicVideoShowcaseContext';
import { useShowcaseContextValue } from './useShowcaseContextValue';

interface ShowcaseEditorProps {
  showcase: MusicVideoShowcaseResponse;
  songMap: MusicVideoSongMap;
  isEmbed: boolean;
}

const nothing = () => undefined;

/**
 * The editor in showcase mode on the showcase answer (spec 2026-10-10 Studio showcase §4): the
 * director's cut of "Forests" as the document, the public audio, the clips of the cut. It holds the
 * sign-up card of the actions that the demo does not run. A reload starts again from the snapshot.
 */
const ShowcaseEditorComponent: React.FC<ShowcaseEditorProps> = ({ showcase, songMap, isEmbed }) => {
  const { editor: data, cut, audioUrl, clips } = showcase;
  const context = useShowcaseContextValue(isEmbed);
  const durationSec = data.song.durationSec ?? cut.doc.settings.endSec;
  const initialDoc = useMemo(() => {
    const parsed = parseMusicVideoDoc(cut.doc, { durationSec });
    return parsed.ok ? parsed.doc : defaultMusicVideoDoc(durationSec);
  }, [cut.doc, durationSec]);

  return (
    <MusicVideoShowcaseContext.Provider value={context}>
      <MusicVideoEditor
        mode="showcase"
        accountId={0}
        catalogItemUuid={SHOWCASE_ITEM_KEY}
        cut={cut}
        initialDoc={initialDoc}
        data={data}
        songMap={songMap}
        readOnly={false}
        onLeave={nothing}
        onOpenCut={nothing}
        audioUrl={audioUrl}
        seedClips={clips}
      />
    </MusicVideoShowcaseContext.Provider>
  );
};

export const ShowcaseEditor = TypedMemo(ShowcaseEditorComponent);
